/**
 * Global Order Notification Provider
 *
 * Subscribes to the two new-order WebSocket events and plays notification
 * sounds on ALL pages in the admin panel (not just the order pages):
 *   - `order:new`       — Deeliver app orders   (default Sound 2)
 *   - `uber:new-order`  — Uber Eats orders      (default Sound 1)
 *
 * Also shows a dismissable MUI Snackbar toast with the order number.
 *
 * Each channel's sound settings (mute, volume, sound selection) are controlled
 * from OrderSoundControls — the Orders page for Deeliver orders, the Uber
 * Orders page for Uber Eats orders — and persisted via useUserPreferences.
 * This provider simply reads those settings and calls playSound().
 */

import React, { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useOrderSound } from '../hooks/useOrderSound';
import { useSocketIO } from './SocketIOProvider';
import { subscribeStoreIds, getStoreIds } from '../components/storeIdsStore';

export function OrderNotificationProvider({ children }: { children: React.ReactNode }) {
  const { playSound: playDeeliverSound } = useOrderSound('deeliver');
  const { playSound: playUberSound } = useOrderSound('uber');
  const { onNewOrder, onUberNewOrder, subscribeAllOrders, subscribeUberOrders, isConnected } = useSocketIO();

  const [toast, setToast] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  // Join every store's admin room so order:new reaches the whole admin. The ids are the
  // stores' externalIds (what the backend broadcasts to), loaded app-wide by the app bar
  // from /admin/stores into storeIdsStore. This used to call GET /stores, which does not
  // exist: the 404 was swallowed and the admin never joined, so new orders only showed
  // after a refresh. Re-runs on reconnect and when the store list changes.
  const storeIds = useSyncExternalStore(subscribeStoreIds, getStoreIds);
  const storeKey = storeIds.join(',');
  useEffect(() => {
    if (!isConnected || !storeKey) return;
    subscribeAllOrders(storeKey.split(','));
  }, [isConnected, storeKey, subscribeAllOrders]);

  // Join the Uber orders room for the whole session (rejoined on every reconnect).
  // Nothing unsubscribes it — the Uber Orders page only listens.
  useEffect(() => {
    if (!isConnected) return;
    subscribeUberOrders();
  }, [isConnected, subscribeUberOrders]);

  // Deeliver app orders
  useEffect(() => {
    return onNewOrder((event: any) => {
      playDeeliverSound();
      const orderNumber = event?.orderNumber || event?.number || 'New';
      setToast({ open: true, message: `New Deeliver order received: #${orderNumber}` });
    });
  }, [onNewOrder, playDeeliverSound]);

  // Uber Eats orders
  useEffect(() => {
    return onUberNewOrder((event) => {
      playUberSound();
      const orderNumber = event?.displayId || 'New';
      setToast({ open: true, message: (event as { due?: boolean } | undefined)?.due ? `Scheduled Uber Eats order due: #${orderNumber}` : `New Uber Eats order received: #${orderNumber}` });
    });
  }, [onUberNewOrder, playUberSound]);

  const handleCloseToast = useCallback((_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="info"
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
